// src/features/notifications/services/notificationService.js
import { useDataEngine } from '@dhis2/app-runtime';
import React from 'react';

// Create a custom hook for notification service
export const useNotificationService = () => {
    const engine = useDataEngine();

    // Get the owner of a parent/higher-level objective
  const getParentObjectiveOwner = async (parentObjectiveId) => {
    console.log('🔍 [NOTIFICATION] Starting getParentObjectiveOwner for:', parentObjectiveId);
    
    try {
        // STEP 1: Get the event with storedBy/createdBy
        console.log('🔄 [NOTIFICATION] Step 1: Fetching event...');
        const eventResponse = await engine.query({
            parentEvent: {
                resource: `events/${parentObjectiveId}`,
                params: {
                    fields: 'trackedEntityInstance,storedBy,createdBy,dataValues[dataElement,value]'
                }
            }
        });

        console.log('✅ [NOTIFICATION] Event response:', eventResponse.parentEvent);
        
        const event = eventResponse.parentEvent;
        if (!event) {
            console.error('❌ [NOTIFICATION] Event not found');
            throw new Error('Parent objective event not found');
        }

        const storedBy = event.storedBy;
        const createdBy = event.createdBy;
        
        console.log('✅ [NOTIFICATION] Found storedBy:', storedBy, 'createdBy:', createdBy);
        
        // STEP 2: Try to get user by storedBy/createdBy (these are USERNAMES, not UIDs)
        const possibleUsernames = [];
        
        if (storedBy) possibleUsernames.push(storedBy);
        if (createdBy && createdBy !== storedBy) possibleUsernames.push(createdBy);
        
        console.log('🔍 [NOTIFICATION] Trying usernames:', possibleUsernames);
        
        for (const username of possibleUsernames) {
            try {
                const user = await getUserByUsername(username);
                if (user && user.email) {
                    console.log('✅ [NOTIFICATION] Found owner via username:', username, user);
                    return user;
                }
            } catch (error) {
                console.warn('⚠️ [NOTIFICATION] Username lookup failed for', username, error);
            }
        }

        // STEP 3: If storedBy/createdBy are actually UIDs (like xFu0DuhOlLG), try direct user endpoint
        console.log('🔍 [NOTIFICATION] Step 3: Trying storedBy/createdBy as user UIDs...');
        const possibleUserIds = [];
        
        // Check if storedBy/createdBy look like UIDs (DHIS2 UIDs are 11 chars)
        if (storedBy && storedBy.length === 11) possibleUserIds.push(storedBy);
        if (createdBy && createdBy.length === 11 && createdBy !== storedBy) possibleUserIds.push(createdBy);
        
        console.log('🔍 [NOTIFICATION] Possible user IDs:', possibleUserIds);
        
        for (const userId of possibleUserIds) {
            try {
                console.log('🔍 [NOTIFICATION] Trying direct user endpoint for UID:', userId);
                const userResponse = await engine.query({
                    user: {
                        resource: `users/${userId}`,
                        params: {
                            fields: 'id,displayName,email,userCredentials[username]'
                        }
                    }
                });
                
                if (userResponse.user && userResponse.user.email) {
                    console.log('✅ [NOTIFICATION] Found owner via user ID:', userId, userResponse.user);
                    return userResponse.user;
                }
            } catch (error) {
                console.warn('⚠️ [NOTIFICATION] User ID lookup failed for', userId, error);
            }
        }

        // STEP 4: Try TEI as fallback
        const teiId = event.trackedEntityInstance;
        if (teiId) {
            console.log('🔍 [NOTIFICATION] Step 4: Falling back to TEI lookup:', teiId);
            try {
                const teiResponse = await engine.query({
                    parentObjective: {
                        resource: `trackedEntityInstances/${teiId}`,
                        params: {
                            fields: 'trackedEntityInstance,attributes[attribute,value],trackedEntityType'
                        }
                    }
                });

                console.log('✅ [NOTIFICATION] TEI response:', teiResponse.parentObjective);

                const parentObjective = teiResponse.parentObjective;
                
                if (parentObjective && parentObjective.attributes) {
                    // Look for username in attributes
                    const usernameAttributes = ['g3RRIfDiQra', 'username', 'userName', 'email'];
                    
                    for (const attrName of usernameAttributes) {
                        const usernameAttr = parentObjective.attributes.find(attr => 
                            attr.attribute === attrName && attr.value
                        );
                        
                        if (usernameAttr?.value) {
                            console.log(`✅ [NOTIFICATION] Found username via attribute ${attrName}:`, usernameAttr.value);
                            try {
                                const user = await getUserByUsername(usernameAttr.value);
                                if (user) return user;
                            } catch (error) {
                                console.warn('⚠️ [NOTIFICATION] Attribute username lookup failed:', error);
                            }
                        }
                    }
                }
            } catch (teiError) {
                console.warn('⚠️ [NOTIFICATION] TEI lookup failed:', teiError);
            }
        }

        // If we reach here, all strategies failed
        console.error('❌ [NOTIFICATION] All strategies failed to find parent objective owner');
        throw new Error(`Parent objective owner not found. Tried: storedBy=${storedBy}, createdBy=${createdBy}, TEI=${teiId}`);

    } catch (error) {
        console.error('❌ [NOTIFICATION] Failed to get parent objective owner:', error);
        throw error;
    }
};

    // Helper function for username lookup
    const getUserByUsername = async (username) => {
        try {
            const userResponse = await engine.query({
                user: {
                    resource: 'users',
                    params: {
                        filter: `userCredentials.username:eq:${username}`,
                        fields: 'id,displayName,email,userCredentials[username]',
                        paging: false
                    }
                }
            });

            if (userResponse.user?.users?.[0]) {
                const user = userResponse.user.users[0];
                console.log('✅ [GET USER BY USERNAME] Found:', user);
                return user;
            }
            throw new Error(`User ${username} not found`);
        } catch (error) {
            console.warn('⚠️ [GET USER BY USERNAME] Failed for', username, error);
            throw error;
        }
    };

    // Get current user display name
    const getCurrentUserDisplayName = () => {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        return currentUser.displayName || currentUser.username || 'Unknown User';
    };

const getReportingManagerForCurrentUser = async () => {
    console.log('🔍 [NOTIFICATION] Getting reporting manager for current user...');
    
    try {
        // Get current position TEI from localStorage
        const currentPositionTei = localStorage.getItem('userPositionTei');
        console.log('✅ Current position TEI:', currentPositionTei);
        
        if (!currentPositionTei) {
            console.error('❌ No userPositionTei in localStorage');
            return null;
        }
        
        // Get current position to find reportingTo
        const positionResponse = await engine.query({
            position: {
                resource: `trackedEntityInstances/${currentPositionTei}`,
                params: { fields: 'attributes[attribute,value]' }
            }
        });
        
        const reportingToAttr = positionResponse.position?.attributes?.find(
            a => a.attribute === 'SjP47qi3JeB'
        );
        
        if (!reportingToAttr?.value) {
            console.warn('⚠️ No reporting manager position found');
            return null;
        }
        
        const managerPositionTei = reportingToAttr.value;
        console.log('✅ Manager position TEI:', managerPositionTei);
        
        // Get manager position
        const managerPosition = await engine.query({
            managerPosition: {
                resource: `trackedEntityInstances/${managerPositionTei}`,
                params: { fields: 'attributes[attribute,value]' }
            }
        });
        
        console.log('🔍 Manager position attributes:', 
            managerPosition.managerPosition?.attributes?.map(a => `${a.attribute}=${a.value}`));
        
        // Look for staff/user reference - IMPORTANT: This could be a USER ID or STAFF TEI ID
        const staffUserIdAttr = managerPosition.managerPosition?.attributes?.find(
            a => a.attribute === 'V66IFsw5kq6' || a.attribute === 'bSVRmRIsB3L'
        );
        
        if (!staffUserIdAttr?.value) {
            console.error('❌ No staff/user reference found in manager position');
            return null;
        }
        
        const staffUserId = staffUserIdAttr.value;
        console.log('🔍 Staff/User ID found:', staffUserId, 'via attribute:', staffUserIdAttr.attribute);
        
        // Check if it's a user ID (11 chars) or TEI ID
        if (staffUserId.length === 11) {
            // Likely a USER ID - try direct user endpoint
            console.log('🔍 Looks like a user ID, trying direct user endpoint...');
            try {
                const userResponse = await engine.query({
                    user: {
                        resource: `users/${staffUserId}`,
                        params: { fields: 'id,displayName,email,userCredentials[username]' }
                    }
                });
                
                if (userResponse.user?.email) {
                    console.log('✅ Found manager via direct user ID:', userResponse.user);
                    return userResponse.user;
                }
            } catch (userError) {
                console.warn('⚠️ Direct user lookup failed, trying as TEI...', userError);
            }
        }
        
        // If not a user ID or user lookup failed, try as TEI
        console.log('🔍 Trying as TEI ID...');
        const staffTei = await engine.query({
            staff: {
                resource: `trackedEntityInstances/${staffUserId}`,
                params: { fields: 'attributes[attribute,value]' }
            }
        });
        
        // Find username in TEI
        const usernameAttr = staffTei.staff?.attributes?.find(
            a => a.attribute === 'g3RRIfDiQra'
        );
        
        if (!usernameAttr?.value) {
            console.error('❌ No username found in staff TEI');
            return null;
        }
        
        const username = usernameAttr.value;
        console.log('✅ Found username in TEI:', username);
        
        // Get user by username
        const userResponse = await engine.query({
            user: {
                resource: 'users',
                params: {
                    filter: `userCredentials.username:eq:${username}`,
                    fields: 'id,displayName,email,userCredentials[username]',
                    paging: false
                }
            }
        });
        
        const manager = userResponse.user?.users?.[0];
        
        if (!manager) {
            console.error('❌ User not found for username:', username);
            return null;
        }
        
        console.log('✅ Manager found:', {
            id: manager.id,
            username: manager.userCredentials?.username,
            email: manager.email,
            displayName: manager.displayName
        });
        
        return manager;
        
    } catch (error) {
        console.error('❌ Error getting reporting manager:', error);
        return null;
    }
};

    // Send email notification to reporting manager
    const sendObjectiveEmailNotification = async (objectiveData, parentObjectiveId) => {
        console.log('🔍 [NOTIFICATION] Starting sendObjectiveEmailNotification', { objectiveData, parentObjectiveId });
        
        try {
            // 🎯 Get reporting manager
            console.log('🔍 [NOTIFICATION] Step 1: Getting reporting manager...');
            
            let manager = null;
            
            try {
                // First try to get reporting manager
                manager = await getReportingManagerForCurrentUser();
                if (!manager) {
                console.warn('⚠️ [NOTIFICATION] No manager found via getReportingManagerForCurrentUser');
                throw new Error('No manager found');
            }

            } catch (error) {
                console.warn('⚠️ [NOTIFICATION] Failed to get reporting manager:', error);
                
                
                console.log('🔄 [NOTIFICATION] Falling back to parent objective owner lookup');
                try {
                    manager = await getParentObjectiveOwner(parentObjectiveId);
                } catch (fallbackError) {
                    console.error('❌ [NOTIFICATION] Fallback also failed:', fallbackError);
                }
            }

            if (!manager) {
                console.error('❌ [NOTIFICATION] No manager found');
                throw new Error('No manager found for notification');
            }

            console.log('✅ [NOTIFICATION] Manager found:', {
                id: manager.id,
                username: manager.userCredentials?.username || manager.username,
                email: manager.email,
                displayName: manager.displayName
            });

            // Get current user display name
            const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
            const currentUserName = currentUser.displayName || currentUser.username || 'Unknown User';

                      // 🎯 Update email content for manager
            const emailData = {
                to: manager.email,
                subject: `New Objective Created by ${currentUserName}: ${objectiveData.name}`,
                message: `
Dear ${manager.displayName},

${currentUserName} has created a new objective that reports to you.

**Objective Details:**
- Name: ${objectiveData.name}
- Description: ${objectiveData.description || 'No description provided'}
- Target Date: ${objectiveData.targetDate ? new Date(objectiveData.targetDate).toLocaleDateString() : 'Not specified'}
- Created By: ${currentUserName}
- Objective ID: ${objectiveData.id}
${parentObjectiveId ? `- Aligns with your objective ID: ${parentObjectiveId}` : ''}

This objective has been created as part of your team's performance goals.

Please review this objective in the Performance Management System.

Best regards,
Performance Management System
                `,
                htmlMessage: `
                    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                        <h2 style="color: #2c3e50;">New Objective Created by Team Member</h2>
                        <p>Dear ${manager.displayName},</p>
                        <p><strong>${currentUserName}</strong> has created a new objective that reports to you.</p>
                        
                        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
                            <h3 style="color: #3498db; margin-top: 0;">Objective Details:</h3>
                            <p><strong>Name:</strong> ${objectiveData.name}</p>
                            <p><strong>Description:</strong> ${objectiveData.description || 'No description provided'}</p>
                            <p><strong>Target Date:</strong> ${objectiveData.targetDate ? new Date(objectiveData.targetDate).toLocaleDateString() : 'Not specified'}</p>
                            <p><strong>Created By:</strong> ${currentUserName}</p>
                            <p><strong>Objective ID:</strong> ${objectiveData.id}</p>
                            ${parentObjectiveId ? `<p><strong>Aligns with your objective ID:</strong> ${parentObjectiveId}</p>` : ''}
                        </div>
                        
                        <p>This objective has been created as part of your team's performance goals.</p>
                        
                        <p>Please review this objective in the Performance Management System.</p>
                        
                        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
                            <p>This is an automated notification from the Performance Management System.</p>
                        </div>
                    </div>
                `
            };

            console.log('📧 [NOTIFICATION] Email data prepared:', {
                to: emailData.to,
                subject: emailData.subject,
                messageLength: emailData.message.length
            });

            // 🎯 Send to manager
            console.log('📧 [NOTIFICATION] Step 2: Sending email to reporting manager...');
            let result;
            
            try {
                // Try to send via DHIS2 messages API
                result = await engine.mutate({
                    resource: 'messageConversations',
                    type: 'create',
                    data: {
                        subject: emailData.subject,
                        text: emailData.message,
                        users: [{ id: manager.id }],
                        organisationUnits: [],
                        userGroups: []
                    }
                });
                
                console.log('✅ [NOTIFICATION] Message sent successfully to reporting manager:', result);
                
                if (!result) {
                    throw new Error('No response from message API');
                }
                
                return {
                    success: true,
                    message: 'Email notification sent successfully to reporting manager',
                    response: result,
                    notificationId: result.response?.uid || result.uid || 'unknown'
                };
                
            } catch (apiError) {
                console.warn('⚠️ [NOTIFICATION] DHIS2 message API failed:', apiError);
                
                // Fallback: Try email API if message API fails
                try {
                    console.log('📧 [NOTIFICATION] Trying email API fallback...');
                    
                    result = await engine.mutate({
                        resource: 'email',
                        type: 'create',
                        data: {
                            to: emailData.to,
                            subject: emailData.subject,
                            text: emailData.message,
                            html: emailData.htmlMessage
                        }
                    });
                    
                    console.log('✅ [NOTIFICATION] Email sent via fallback to reporting manager:', result);
                    
                    return {
                        success: true,
                        message: 'Email sent via fallback API to reporting manager',
                        response: result
                    };
                    
                } catch (emailError) {
                    console.error('❌ [NOTIFICATION] Email API also failed:', emailError);
                    
                    // Log to console as last resort
                    console.log('📋 [NOTIFICATION] Email content (for manual sending):', {
                        to: emailData.to,
                        subject: emailData.subject,
                        body: emailData.message
                    });
                    
                    throw new Error(`Failed to send notification to reporting manager: ${emailError.message}`);
                }
            }

        } catch (error) {
            console.error('❌ [NOTIFICATION] Failed to send email notification:', error);
            
            return {
                success: false,
                error: error.message || 'Unknown error',
                details: 'Check notification service configuration',
                stack: error.stack
            };
        }
    };

    return {
        getParentObjectiveOwner,
        sendObjectiveEmailNotification,
        getReportingManagerForCurrentUser,
        getUserByUsername,
        getCurrentUserDisplayName
    };
};

export default useNotificationService;